import { useState } from 'react';
import { api, type Permission, type Role } from '../../api';
import { can, useData, useMe } from '../../useData';
import { ErrorMsg, Modal } from '../../components/Modal';

export function RolesPage() {
  const me = useMe();
  const { data: roles, reload, error } = useData<Role[]>('/admin/roles');
  const { data: permissions } = useData<Permission[]>('/admin/permissions');
  const [editing, setEditing] = useState<Role | 'System' | 'Workspace' | null>(null);
  const editable = can(me, 'admin.roles.manage');

  const section = (scope: 'System' | 'Workspace', title: string, hint: string) => (
    <section>
      <div className="section-head">
        <h2>{title}</h2>
        {editable && <button onClick={() => setEditing(scope)}>+ Rol nou</button>}
      </div>
      <p className="muted small">{hint}</p>
      <table>
        <thead><tr><th>Rol</th><th>Descriere</th><th>Permisiuni</th><th /></tr></thead>
        <tbody>
          {(roles ?? []).filter((r) => r.scope === scope).map((r) => (
            <tr key={r.id}>
              <td><strong>{r.name}</strong>{r.isBuiltIn && <span className="tag">predefinit</span>}</td>
              <td className="muted">{r.description}</td>
              <td>{r.permissionIds.length}</td>
              <td className="right">{editable && <button className="link" onClick={() => setEditing(r)}>Editează</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );

  return (
    <>
      <div className="page-head"><h1>Roluri și permisiuni</h1></div>
      <ErrorMsg error={error} />
      {section('System', 'Niveluri de acces în aplicație', 'Administrator / Supervizor / Utilizator — ce poate face cineva în aplicație în general.')}
      {section('Workspace', 'Roluri în workspace', 'Ce poate face un membru în interiorul unui workspace (ca în Podio).')}
      {editing && permissions && (
        <RoleForm role={typeof editing === 'string' ? null : editing}
          scope={typeof editing === 'string' ? editing : editing.scope}
          permissions={permissions} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}
    </>
  );
}

function RoleForm(props: { role: Role | null; scope: 'System' | 'Workspace'; permissions: Permission[]; onClose: () => void; onSaved: () => void }) {
  const r = props.role;
  const [name, setName] = useState(r?.name ?? '');
  const [code, setCode] = useState(r?.code ?? (props.scope === 'System' ? 'sys.' : 'ws.'));
  const [description, setDescription] = useState(r?.description ?? '');
  const [selected, setSelected] = useState<Set<number>>(new Set(r?.permissionIds ?? []));
  const [error, setError] = useState<string | null>(null);
  const perms = props.permissions.filter((p) => p.scope === props.scope);

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function save() {
    setError(null);
    try {
      const body = { name, description: description || null, permissionIds: [...selected] };
      if (r) await api.put(`/admin/roles/${r.id}`, body);
      else await api.post('/admin/roles', { ...body, code, scope: props.scope });
      props.onSaved();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Modal title={r ? `Rol: ${r.name}` : 'Rol nou'} onClose={props.onClose} wide>
      <div className="row">
        <label>Nume<input value={name} onChange={(e) => setName(e.target.value)} /></label>
        {!r && <label>Cod intern<input value={code} onChange={(e) => setCode(e.target.value)} /></label>}
      </div>
      <label>Descriere<input value={description} onChange={(e) => setDescription(e.target.value)} /></label>
      {r?.code === 'sys.admin' && <p className="muted small">Accesul la administrare rămâne mereu activ pentru rolul Administrator.</p>}
      <div className="perm-list">
        {perms.map((p) => (
          <label key={p.id} className="checkbox">
            <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
            {p.description} <span className="muted small">{p.code}</span>
          </label>
        ))}
      </div>
      <ErrorMsg error={error} />
      <div className="actions">
        <button onClick={props.onClose}>Renunță</button>
        <button className="primary" disabled={name.trim().length < 2} onClick={save}>Salvează</button>
      </div>
    </Modal>
  );
}
